/**
 * useMosaic.ts
 */
import Event from '@shared/classes/event';
import Focus from '@shared/types/enums/focus';
import MosaicProps from '@shared/types/props/mosaic';
import Setting from '@shared/classes/setting';
import goToContent from '@shared/actions/content/goToContent';
import useMosaicKeyboard from '@components/templates/mosaic/lib/useMosaicKeyboard';
import { MOSAIC_FOCUS_BLACKLIST } from '@shared/types/constants/focusVisibility';
import { GridDisplayMode } from '@shared/types/enums/grid';
import { MosaicContextProps } from '@shared/components/utils/context/mosaicContext';
import { selectFocus, setFocus } from '@shared/store/slices/focus.slice';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  selectCurrentParams,
  updateParams as updateParamsSlice,
} from '@shared/store/slices/router.slice';

/**
 * Hook that handles the mosaic template.
 * @param props - The props for the mosaic template.
 * @returns The mosaic context props.
 */
export default function useMosaic(props: MosaicProps): MosaicContextProps {
  // -------------------------------------------------------------
  // Misc Hooks & variables
  // -------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const focus = useAppSelector(selectFocus);
  const currentParams = useAppSelector(selectCurrentParams);

  /**
   * The data to display and flags for the mosaic.
   */
  const { data, flags } = props;

  /**
   * The asset of the currently selected element.
   */
  const [asset, setAsset] = useState<Event | Setting | undefined>(
    (currentParams?.asset as Event) || undefined
  );

  /**
   * The index of the currently selected element.
   */
  const [index, setIndex] = useState((currentParams?.index as number) || 0);

  // -------------------------------------------------------------
  // References
  // -------------------------------------------------------------

  const colsRef = useRef<number>(Math.min(5, data.length));
  const rowsRef = useRef<number>(Math.ceil(data.length / colsRef.current));
  const assetRef = useRef<Event | Setting>();
  const dataRef = useRef(data);
  const flagsRef = useRef(flags);
  const indexRef = useRef(index);
  colsRef.current = Math.min(5, data.length);
  rowsRef.current = Math.ceil(data.length / colsRef.current);
  assetRef.current = asset;
  dataRef.current = data;
  flagsRef.current = flags;
  indexRef.current = index;

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  const config = useMemo(() => {
    const currentRow = Math.floor(index / colsRef.current);
    const minimumIndex = rowsRef.current > 2 ? currentRow * colsRef.current : 0;
    return {
      columns: Math.min(5, data.length),
      rows: Math.ceil(data.length / colsRef.current),

      description: focus !== Focus.MENU && !!flags?.hasDescription,
      minimumIndex,
      displayMode: flags?.hasSettings
        ? GridDisplayMode.SETTING
        : GridDisplayMode.PHOTO,
    };
  }, [data, index, flags, focus]);

  // -------------------------------------------------------------
  // Effect hooks
  // -------------------------------------------------------------

  useEffect(() => {
    if (!MOSAIC_FOCUS_BLACKLIST.includes(focus as number)) {
      dispatch(setFocus(Focus.MOSAIC));
    }
  }, []);

  useEffect(() => {
    if (data.length && !asset) {
      setAsset(data[0] as Event);
    }
  }, [data]);

  // -------------------------------------------------------------
  // Callbacks
  // -------------------------------------------------------------

  /**
   * Changes the asset for the given index.
   * @param index - The index of the element that is being changed.
   */
  const changeAsset = useCallback((index: number) => {
    const asset = dataRef.current[index] as Event;
    setAsset(asset);
  }, []);

  /**
   * Handles the hover event for the asset.
   * @param asset - The asset that is being hovered.
   */
  const hoverAsset = useCallback((asset: Event | Setting) => {
    setAsset(asset);
  }, []);

  /**
   * Selects and accesses or modifies the asset for the given index.
   * @param index - The index of the element that is being selected.
   */
  const selectAsset = useCallback((index: number) => {
    const asset = dataRef.current[index] as Event;
    assetRef.current = asset;
    indexRef.current = index;
    setAsset(asset);
    setIndex(index);
    updateParams();
    if (asset instanceof Setting) {
      (asset as Setting).action();
    } else {
      goToContent(asset);
    }
  }, []);

  /**
   * Updates the parameters for the current route.
   */
  function updateParams() {
    const { focus: focusSlice } = store.getState();
    dispatch(
      updateParamsSlice({
        // Content
        data: dataRef.current,
        index: indexRef.current,
        // Hook params
        asset: assetRef.current,
        complete: flagsRef.current?.complete || false,
        focus: focusSlice.focus,
      })
    );
  }

  // -------------------------------------------------------------
  // Event handlers
  // -------------------------------------------------------------

  const onMouseEnter = useCallback(() => {
    if (focus === Focus.FLOATING_MENU) return;
    dispatch(setFocus(Focus.MOSAIC));
  }, [focus]);

  // -------------------------------------------------------------
  // Keyboard
  // -------------------------------------------------------------

  useMosaicKeyboard({
    colsRef,
    dataRef,
    indexRef,
    setIndex,
    changeAsset,
    selectAsset,
    updateParams,
  });

  // -------------------------------------------------------------
  // Return values
  // -------------------------------------------------------------

  return {
    // Fields
    asset,
    config,
    data,
    index,
    // Event handlers
    onMouseEnter,
    // Callbacks
    hoverAsset,
    selectAsset,
  };
}
